import { useCallback, useRef } from 'react';
import { IBuildingProviderContext, ITexture } from '../../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import ElementModel from '../../../models/standard/ElementModel';
import FloorModel from '../../../models/standard/FloorModel';
import Model2DModel from '../../../models/standard/Model2DModel';
import RoomModel from '../../../models/standard/RoomModel';

// --- Type Guards to handle Plain JSON objects vs Class Instances ---
const isFloor = (el: any): el is FloorModel => el && 'tiles' in el;
const isElement = (el: any): el is ElementModel => el && 'type' in el && 'length' in el;
const isModel2D = (el: any): el is Model2DModel => el && 'fileFormat' in el;

export interface ITextureActions {
  setSelectedTexture: (texture: ITexture | null, immediate?: boolean) => void;
  clearSelectedTexture: () => void;
  applyTexture: (element: FloorModel | ElementModel | Model2DModel) => void;
  getSelectedTexture: () => ITexture | null;

  setSelectedModel: (element: FloorModel | ElementModel | Model2DModel | null) => void;
  getSelectedModel: () => FloorModel | ElementModel | Model2DModel | null;
  applyTextureToStoredObject: () => void;
}

export const useTextureActions = (
  setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>
): ITextureActions => {
  const selectedTextureRef = useRef<ITexture | null>(null);
  const selectedObjectRef = useRef<FloorModel | ElementModel | Model2DModel | null>(null);

  const clearSelectedTexture = useCallback(() => {
    selectedTextureRef.current = null;
  }, []);

  const setSelectedObject = useCallback((element: FloorModel | ElementModel | Model2DModel | null) => {
    selectedObjectRef.current = element;
  }, []);

  const getSelectedObject = useCallback(() => selectedObjectRef.current, []);

  const cloneWithProto = <T extends object>(obj: T): T =>
    Object.assign(Object.create(Object.getPrototypeOf(obj)), obj);

  const cloneFloorWithTexture = (orig: FloorModel, tex: ITexture | null): FloorModel => {
    const fm = new FloorModel(orig.tiles, tex, orig.attributes);
    fm.setUUID(orig.uuid); 
    if (orig.floorShape) fm.setFloorShape(orig.floorShape);
    return fm;
  };

  const replaceFloorTextureInRooms = (
    rooms: RoomModel[] | any[],
    targetUuid: string,
    tex: ITexture | null
  ) => {
    let changed = false;

    const nextRooms = (rooms ?? []).map((room) => {
      const spaces = room?.spaces ?? [];
      let roomChanged = false;

      const nextSpaces = spaces.map((space: any) => {
        const currentFloor = space.floor;

        if (currentFloor && currentFloor.uuid === targetUuid) {
          roomChanged = true;
          changed = true;
          
          const newFloor = cloneFloorWithTexture(currentFloor, tex);

          const spaceClone = cloneWithProto(space);
          spaceClone.floor = newFloor; 
          return spaceClone;
        }

        return space;
      });

      if (roomChanged) {
        const roomClone = cloneWithProto(room);
        roomClone.spaces = nextSpaces;
        return roomClone;
      }
      return room;
    });

    return changed ? nextRooms : rooms;
  };

  const applyTexture = useCallback(
    (element: FloorModel | ElementModel | Model2DModel) => {
      if (!selectedTextureRef.current) return;

      const targetUuid = element.uuid;

      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };

        // 1. Handle Floors
        if (isFloor(element)) {
          const hasRooms = Array.isArray(next.rooms);
          if (hasRooms) {
            next.rooms = replaceFloorTextureInRooms(
              next.rooms,
              element.uuid,
              selectedTextureRef.current
            );
            next.lastAction = EHistoryAction.FLOOR_TEXTURE_CHANGED;
            next.isSetByUndoRedo = false;
            return next;
          }
        } 
        
        // 2. Handle Elements
        else if (isElement(element)) {
          let found = false;

          // A) Try Root Elements
          const nextElements = (next.elements || []).map((el) => {
            if (el.uuid === targetUuid) {
              found = true;
              const newEl = new ElementModel(
                el.type,
                el.length,
                el.width,
                el.position,
                el.rotation,
                selectedTextureRef.current,
                el.description,
                el.useAsTextField,
                el.fontSize,
                el.object,
                el.attributes,
                el.sideC,
                el.triangleAngle
              );
              newEl.setUUID(el.uuid);
              return newEl;
            }
            return el;
          });

          if (found) {
            next.elements = nextElements;
          } else {
            // B) Try inside Spaces
            next.rooms = (next.rooms || []).map((room) => {
              if (!room.spaces) return room;
              const newSpaces = room.spaces.map((space) => {
                if (!space.elements) return space;
                const elIndex = space.elements.findIndex((e) => e.uuid === targetUuid);
                
                if (elIndex !== -1) {
                  const el = space.elements[elIndex];
                  const newEl = new ElementModel(
                    el.type,
                    el.length,
                    el.width,
                    el.position,
                    el.rotation,
                    selectedTextureRef.current,
                    el.description,
                    el.useAsTextField,
                    el.fontSize,
                    el.object,
                    el.attributes,
                    el.sideC,
                    el.triangleAngle
                  );
                  newEl.setUUID(el.uuid);

                  const newSpaceElements = [...space.elements];
                  newSpaceElements[elIndex] = newEl;

                  const spaceClone = cloneWithProto(space);
                  spaceClone.elements = newSpaceElements;
                  return spaceClone;
                }
                return space;
              });
              
              if (newSpaces !== room.spaces) {
                 const roomClone = cloneWithProto(room);
                 roomClone.spaces = newSpaces;
                 return roomClone;
              }
              return room;
            });
          }

          next.lastAction = EHistoryAction.ELEMENT_TEXTURE_CHANGED;
          next.isSetByUndoRedo = false;
          return next;
        } 
        
        // 3. Handle Models2D
        else if (isModel2D(element)) {
          let found = false;

          const nextModels = (next.models2D || []).map((model) => {
            if (model.uuid === targetUuid) {
              found = true;
              const newModel = new Model2DModel(
                model.id,
                model.name,
                model.filePath,
                model.fileFormat,
                model.fileSize,
                model.dimensions.length,
                model.dimensions.depth,
                model.object,
                model.position,
                model.rotation,
                selectedTextureRef.current,
                model.attributes 
              );
              newModel.setUUID(model.uuid);
              return newModel;
            }
            return model;
          });

          if (found) {
            next.models2D = nextModels;
          } else {
            next.rooms = (next.rooms || []).map((room) => {
              if (!room.spaces) return room;
              const newSpaces = room.spaces.map((space) => {
                if (!space.models2D) return space;
                const mIndex = space.models2D.findIndex((m) => m.uuid === targetUuid);

                if (mIndex !== -1) {
                  const model = space.models2D[mIndex];
                  const newModel = new Model2DModel(
                    model.id,
                    model.name,
                    model.filePath,
                    model.fileFormat,
                    model.fileSize,
                    model.dimensions.length,
                    model.dimensions.depth,
                    model.object,
                    model.position,
                    model.rotation,
                    selectedTextureRef.current,
                    model.attributes
                  );
                  newModel.setUUID(model.uuid);

                  const newSpaceModels = [...space.models2D];
                  newSpaceModels[mIndex] = newModel;

                  const spaceClone = cloneWithProto(space);
                  spaceClone.models2D = newSpaceModels;
                  return spaceClone;
                }
                return space;
              });

              if (newSpaces !== room.spaces) {
                const roomClone = cloneWithProto(room);
                roomClone.spaces = newSpaces;
                return roomClone;
              }
              return room;
            });
          }

          next.lastAction = EHistoryAction.MODEL2D_TEXTURE_CHANGED;
          next.isSetByUndoRedo = false;
          return next;
        }

        return prevBuilding;
      });

      selectedTextureRef.current = null;
    },
    [setBuilding],
  );

  const setSelectedTexture = useCallback(
    (texture: ITexture | null, immediate: boolean = false) => {
      selectedTextureRef.current = texture;
      if (!immediate && selectedObjectRef.current && texture) {
        applyTexture(selectedObjectRef.current);
      }
    },
    [applyTexture],
  );

  const applyTextureToStoredObject = useCallback(() => {
    if (selectedObjectRef.current && selectedTextureRef.current) {
      applyTexture(selectedObjectRef.current);
      selectedTextureRef.current = null;
    }
  }, [applyTexture]);

  const getSelectedTexture = useCallback(() => selectedTextureRef.current, []);

  return {
    setSelectedTexture,
    clearSelectedTexture,
    applyTexture,
    getSelectedTexture,
    setSelectedModel: setSelectedObject,
    getSelectedModel: getSelectedObject,
    applyTextureToStoredObject,
  };
};
